import { DataSourceRequestState } from '@progress/kendo-data-query';
import { CommonFilterRequest, DatasetID, FilterParent } from './filter.model';
import { AggregateRequestType, ComposerMode, PageParams } from './page-composer.model';
import { ColumnModel } from './widget-config.model';
import { Claim, Feature } from './page-config.models';
import { KwelloBarChartOptions, KwelloLineChartOptions } from './chart.model';
import { KwelloGridColumnModel } from './kwello-grid.model';
import { KWELLO_ICON } from 'common/components/icon/KwelloSvgIcon';

export type WidgetRequest = DataSourceRequestState | DataSourceRequestState[];

export type AggregateType = {
  name: string;
  caption: string;
  value: number;
  min: number;
  max: number;
  avg: number;
};

export type onFilterClickCallback = (params: {
  filterType: any;
  filterKey: any;
  filterLabel: string;
  value: [{ name: string; value: string | number }];
}) => void;

export interface WidgetRequestWithFilters {
  request: WidgetDataRequestModel;
  filters: CommonFilterRequest;
  includeAlignment?: boolean;
  id: string;
  headers: any;
}

export interface WidgetDataRequestModel {
  path: string;
  data: WidgetRequest;
}

export type WidgetID = string;

export interface GroupList {
  displayName: string;
  memberName: string;
  features?: Feature[];
}

export interface AggregatesOptionsList {
  displayName: string;
  claims?: Claim[];
  aggregatesList: AggregateRequestType[];
}

export interface TabsOptionsList {
  name: string;
  tabId: string;
  icon?: string;
}
export interface SingleAggregateOption {
  filterCategory: FilterParent;
  label: string;
  sourceField: string;
  aggregateMethodName: string;
  path: string;

  claims: Claim[];
  features: Feature[];
}

interface WidgetTabModel {
  tabId: string;
  defaultAdditionalColumns?: ColumnModel[];
  additionalColumns?: ColumnModel[];
  request: WidgetDataRequestModel;
}

export type WidgetId = string;

export type WidgetTabItem = {
  title: string;
  widgetCatalogId: string;
  icon?: KWELLO_ICON;
};

export interface WidgetModel {
  additionalColumns?: ColumnModel[];
  aggregateOptions?: AggregatesOptionsList[];
  barChartOptions?: Partial<KwelloBarChartOptions>;
  categoryFilterName?: string;
  claims?: Claim[];
  className?: string;
  config?: {
    columns?: ColumnModel[];
    bryntumColumns?: KwelloGridColumnModel[];
    childrenField?: string;
    defaultSortField?: string;
    defaultSortDirection?: 'asc' | 'desc';
  };
  col: number;
  colSpan: number;
  dataType: WidgetDataType;
  datasetId: string;
  defaultAdditionalColumns?: ColumnModel[];
  defaultFilters?: any; // This has to be Any for the moment because we seem to jam multiple conflicting types in here across the app :(
  defaultGroupBy?: string;
  enableSearch: boolean;
  excelExportAllData: boolean;
  expandable?: boolean;
  exportOptions: WidgetActionOption[];
  features?: Feature[];
  filterAttrGroup?: string[];
  groupOptions: GroupList[];
  groupable?: boolean;
  hasQuestion?: boolean;
  headerTabs?: TabsOptionsList[]; // This is only here for backward compatibility, use `tabs` instead.
  hidden: boolean;
  id: WidgetId;
  labels?: { [key: string]: string };
  lineChartOptions?: Partial<KwelloLineChartOptions>;
  maxColumnSelection?: number;
  maxH?: number;
  maxW?: number;
  minH?: number;
  minW?: number;
  mode?: ComposerMode;
  noHeader?: boolean;
  order?: number;
  originalWidgetId?: string;
  originalTitle?: string;
  pageTitle?: string;
  request?: WidgetDataRequestModel; // Request is optional because GKP Bryntum widgets handle their own requests.
  requestFilters: RequestFilter[];
  rowSpan: number;
  rowSpanBreakPoints?: { [key: string]: number };
  secondaryPath?: string;
  selectedAggregates?: AggregateRequestType[];
  selectedGroupBy?: string;
  showLineChartOptions?: boolean;
  showTotals?: boolean;
  singleAggregateOptions?: SingleAggregateOption[];
  subTitle?: string;
  tabs?: WidgetTabItem[];
  title: string;
  type: WidgetType;
  viewToggle?: boolean;
  views: string[];
  widgetConfig?: { [key: string]: any };
  widgetStyle?: React.CSSProperties;
  widgetTabs?: WidgetTabModel[];
  pageParams?: PageParams;
  widgets?: [];
  host?: string;
}

export enum WidgetActionOption {
  AddToReport = 'addToReport',
  ExportPowerpoint = 'powerpoint',
  ExportExcel = 'excel',
  ExcelPartial = 'excel_partial',
  ResetWidgetOptions = 'resetWidgetOptions',
  ExportExcelChart = 'exportExcelChart',
  ExportExcelAllColumns = 'exportExcelAllColumns',
  ExportExcelVisibleColumns = 'exportExcelVisibleColumns',
  IncludeAll = 'includeAll',
  ExcludeAll = 'excludeAll',
}

export enum WidgetType {
  AI_CHAT = 'ai-chat',
  ALIGNMENT_DISTRIBUTION = 'alignment_distribution',
  ALIGNMENT_MOVEMENT = 'alignment_movement',
  ALIGNMENT_OVERVIEW = 'alignment_overview',
  ALIGNMENT_OVERVIEW_TREE = 'alignment_overview_tree',
  ALIGNMENT_PROFILE_QUESTION = 'alignment_profile_question',
  ALIGNMENT_RESPONSE_DETAIL = 'alignment_response_detail',
  ALIGNMENT_SHIFT = 'alignment_shift',
  BAR_CHART = 'bar_chart',
  CATEGORY_RESPONSE_OVERVIEW = 'category_response_overview',
  COACH_DETAIL_TEAM_DETAIL = 'coach_detail_team_detail',
  COACH_OVER_TIME = 'coach_over_time',
  COACH_SUMMARY = 'coach_summary',
  COACH_TEAM_METRIC_PLOT = 'coach_team_metric_plot',
  COUNTER = 'counter',
  ELSIE_AI_EXPORT = 'elsie_export',
  ELSIE_AI_SUMMARY = 'elsieai-summary',
  ELSIE_AI_V2_COUNTER = 'elsie-ai-v2-counter',
  FRACTION_TABLE = 'fraction_table',
  INTERACTION_BY_INTERACTION_DETAILS = 'interaction_interaction_details',
  INTERACTION_CHANNEL_QUARTER = 'interaction_channel_quarter',
  INTERACTION_DETAILS = 'interaction_details',
  INTERACTION_TEAM_MEMBER = 'interaction_team_member',
  INTERACTION_TOPIC_BY_PRODUCT = 'interaction_topic_by_product',
  LATEST_ALIGNMENT = 'latest_alignment',
  MAP = 'map',
  MIR_DETAILS = 'mir_details',
  MIR_PRODUCT = 'mir_product',
  NESTED_WIDGET = 'nested_widget',
  PLANNING_ATTENDEES_LIST = 'planning-attendees-list',
  PLANNING_CALENDAR = 'planning-calendar',
  PLANNING_EVENT_ATTENDEES = 'planning-event-attendees',
  PLANNING_LISTS = 'planning-lists',
  PLANNING_PROFILES_LIST = 'planning-profiles-list',
  PLANNING_PROFILE_DETAILS = 'planning-profile-details',
  PLANNING_PROFILE_ACTIVITIES_PARTICIPATION = 'planning-profile-activity-participation',
  PLANNING_PROFILE_INTERACTIONS = 'planning-profile-interactions',
  PROFILE_INTERACTION_DETAILS = 'profile_interaction_details',
  RESPONSE_DETAILS = 'response_details',
  RESPONSE_DETAILS_EXTENDED = 'response_details_extended',
  RESPONSE_DETAIL_SOURCES = 'response_details_sources',
  RESPONSE_GEO = 'response_geo',
  RESPONSE_OVER_TIME_RESPONSES = 'response_over_time_responses',
  RESPONSE_VOLUME = 'response_volume',
  RESPONSE_VOLUME_OVER_TIME = 'response_volume_over_time',
  SINGLE_METRIC = 'single_metric',
  TABBED_WIDGET = 'tabbed_widget',
  VERTICAL_COUNTER = 'vertical-counter',
  WORD_CLOUD = 'word_cloud',
  KWELLO_KENDO_GRID = 'kwello-kendo-grid',
  KWELLO_BRYNTUM_GRID = 'kwello-bryntum-grid',
  PROJECT_CONFIG = 'project_config',
  KWELLO_BRYNTUM_TREE_GRID = 'kwello-bryntum-tree-grid',
}

export enum RequestFilter {
  FILTER_EXPIRED_CATEGORIES = 'filter_expired_categories',
  FILTER_SHOW_TEAM_NAMES_ON_PLOT = 'show_team_names_on_plot',
}

export interface FieldIdItem {
  id: string;
  claims: Claim[];
}

export interface CoachViewItems {
  fieldIds: FieldIdItem[];
  defaultIds: FieldIdItem[];
}

export interface ChartLabels {
  [key: string]: string;
}

export type OnDataSliceClick = (
  prop: string,
  value: string | number,
  displayValue?: string | number
) => void;

export interface WidgetProps<T = unknown> extends DatasetID, WidgetModel {
  id: string;
  labels?: ChartLabels;
  data: T;
  exportShadowData?: T;
  onDataSliceClick: OnDataSliceClick;
  groupBy: any;
  showTotals?: boolean;
  hideLegend?: boolean;
  showSpecialtyOnLabels?: boolean;
  startDate: string;
  endDate: string;
  filterState: any;
  sortState: any;
  widget: WidgetModel;
  height?: number;
  width?: number;
  onFilterClick: onFilterClickCallback;
  isLoading?: boolean;
  isFetching?: boolean;
}

export enum WidgetDataType {
  ELSIE_AI_V2_COUNTER = 'elsie-ai-v2-counter',
  SERIES = 'series',
  LIST = 'list',
  GEO = 'geo',
  COUNTER = 'counter',
  SINGLE_METRIC = 'single_metric',
  PLANNING_KWELLO_GRID = 'planning-kwello-grid',
  PLANNING_PROFILES_COUNTER = 'planning-profiles-counter',
  PLANNING_PROFILES_LIST = 'planning-profiles-list',
  PLANNING_LISTS = 'planning-lists',
  PLANNING_ATTENDEES_LIST = 'planning-attendees-list',
  PLANNING_PROFILE_DETAILS = 'planning-profile-details',
  PLANNING_PROFILE_DETAILS_COUNTERS = 'planning-profile-details-counters',
  PLANNING_PROFILE_ACTIVITIES_PARTICIPATION = 'planning-profile-activity-participation',
  PLANNING_PROFILE_INTERACTIONS = 'planning-profile-interactions',
  STATIC = 'static',
  PROJECT_CONFIG = 'project_config',
}

export interface Aggregator {
  name: string;
  value: number;
  caption?: string;
  maxValue?: number;
}

export interface WidgetViewToggleModel {
  toggles: { [key: string]: number };
}

export interface GeographyRegionColor {
  rgb: string;
  hex: string;
  alpha: number;
}

export interface Aggregate {
  name: string;
  value: number;
  alignmentColor1?: string;
  alignmentColor2?: string;
  caption?: string;
}
